import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2, ExternalLink, Building2, TrendingUp } from 'lucide-react';

/**
 * Public page: /venteliste/trekk-meg/:token
 *
 * Kandidat får denne lenken i venteliste-e-post. GET henter info (uten Vipps),
 * POST trekker plassen. Backend har signert UUID-token med rate-limit implisitt
 * via publicEndpoints.
 */

interface PreviewData {
  candidate: {
    name: string;
    status: string;
    approval_status: string;
    waitlist_position: number | null;
    already_cancelled: boolean;
  };
  instance: {
    name: string;
    start_date: string | null;
    location: string | null;
    host: string;
  };
}

export function WaitlistWithdrawPage() {
  const { token } = useParams<{ token: string }>();
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`/api/public/waitlist/withdraw/${token}`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Fant ikke lenken');
        }
        setPreview(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Kunne ikke laste');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!token) return;
    setConfirming(true);
    try {
      const res = await fetch(`/api/public/waitlist/withdraw/${token}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || 'Kunne ikke trekke deg fra venteliste');
        return;
      }
      setCompleted(true);
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-kf-blue-700" />
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-red-200 p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Lenken er ugyldig</h1>
          <p className="text-gray-600">{error || 'Denne lenken er ugyldig eller utløpt.'}</p>
        </div>
      </div>
    );
  }

  const { candidate, instance } = preview;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-kf-blue-800 to-kf-blue-950 px-6 py-5 text-white border-b-4 border-kf-orange-600">
            <h1 className="text-2xl font-bold">Trekk meg fra venteliste</h1>
            <p className="text-kf-blue-100 mt-1 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {instance.host}
            </p>
          </div>

          <div className="p-6 space-y-4">
            {completed ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center space-y-3">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
                <h2 className="text-xl font-bold text-green-900">Du er trukket fra ventelisten</h2>
                <p className="text-green-800">
                  Vi har oppdatert påmeldingen din. Du vil ikke lenger få tilbud om plass hvis noen andre trekker seg.
                </p>
                <a
                  href="https://kompetanseheving.no"
                  className="inline-flex items-center gap-1 text-kf-blue-700 hover:underline pt-2"
                >
                  Tilbake til kompetanseheving.no <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            ) : candidate.already_cancelled ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center space-y-2">
                <CheckCircle2 className="w-12 h-12 text-gray-500 mx-auto" />
                <h2 className="text-lg font-bold text-gray-900">Allerede trukket</h2>
                <p className="text-gray-700">Du står ikke lenger på ventelisten.</p>
              </div>
            ) : candidate.status !== 'waitlist' ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                <AlertCircle className="w-8 h-8 text-amber-600 mb-3" />
                <h2 className="text-lg font-bold text-amber-900 mb-1">Ikke på venteliste</h2>
                <p className="text-amber-800 text-sm">
                  Denne påmeldingen har status «{candidate.status}» — ikke venteliste.
                  Bruk «Min påmelding»-lenken i e-posten din i stedet.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <strong>{candidate.name}</strong> — venteliste posisjon #{candidate.waitlist_position} til
                    kurset <strong>{instance.name}</strong>.
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded p-4 text-sm text-gray-800 space-y-2">
                  <p>Trekker du deg fra ventelisten:</p>
                  <ul className="list-disc list-inside space-y-1 pl-2 text-gray-700">
                    <li>Vil du ikke lenger få tilbud om plass hvis noen andre trekker seg</li>
                    <li>Kan du melde deg på igjen senere hvis det fortsatt er ledige plasser</li>
                    <li>Betyr det ikke at kurset er avlyst for andre</li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-end pt-2">
                  <Link
                    to="/"
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded text-center"
                  >
                    Avbryt
                  </Link>
                  <button
                    onClick={submit}
                    disabled={confirming}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-semibold disabled:opacity-50"
                  >
                    {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
                    Trekk meg fra ventelisten
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-gray-500">
          En tjeneste fra <strong className="text-kf-blue-950">Kompetanseforbundet</strong>
        </div>
      </div>
    </div>
  );
}
